import { CaretDown, CaretUp, ChevronRight, Download } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import {
  downloadSalesforceProfilesReport,
  getSalasForceUserPaginatedData,
  getSalesforceProfiles
} from '../../api/salesforceAudit/salesforceAudit.service'

import { Button } from '../../components/New/Button/Button'
import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import Table from '../../components/common/DDS/Table/Table'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { paginate } from '../../utils/CommonUtils'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useNavigate } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFProfilesOverview: React.FC = () => {
  const navigate = useNavigate()
  const store = useStoreContext()
  const { salesforceAuditStore } = useStoreContext()
  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId

  const [currentPage, setCurrentPage] = useState(1)
  const [profilesData, setProfilesData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [userLoading, setUserLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)

  const [usersWithLicense, setUsersWithLicense] = useState<any[]>([])

  const fetchSalesforcePaginatedUsers = async () => {
    store.uiStore.setGlobalLoader(true)
    setUserLoading(true)

    try {
      const response: any = await getSalasForceUserPaginatedData({
        $salesforceAuditId: salesforceAuditId as string,
        page: currentPage,
        size: SalesforceAuditStore.userPageSize
      })

      if (response) {
        setUsersWithLicense(response.data)
        salesforceAuditStore.setUserLicensePaginatedData(response.data)
      }
    } catch (err) {
      console.error('Error fetching Salesforce users:', err)
    } finally {
      setUserLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  const getUsersAssignedForProfile = (profileId: string) => {
    const filtered = usersWithLicense.filter((user: any) => {
      return user?.record?.Profile?.Id === profileId
    })

    return filtered
  }

  useEffect(() => {
    if (salesforceAuditId) {
      fetchSalesforcePaginatedUsers()
    }
  }, [salesforceAuditId, currentPage])

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  useEffect(() => {
    const fetchProfiles = async () => {
      if (!salesforceAuditId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await getSalesforceProfiles({ id: salesforceAuditId })

        const profile = response.data.map((item: any) => item.record)
        salesforceAuditStore.setProfilesData(response?.data)
        setProfilesData(profile)
      } catch (err: any) {
        console.error('Error fetching profiles:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchProfiles()
  }, [salesforceAuditId])

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const filteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) {
      return profilesData
    }

    return profilesData.filter((item: any) => {
      const name = (item?.Name || '').toLowerCase()
      const description = (item?.Description || '').toLowerCase()
      const modifiedBy = item?.LastModifiedBy?.Name?.toLowerCase() || ''

      const usersAssignedCount = (
        getUsersAssignedForProfile(item?.Id)?.length || 0
      ).toString()

      // Prepare flexible date formats
      let formattedDates: string[] = []
      if (item?.LastModifiedDate) {
        const m = moment(item.LastModifiedDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('MMMM').toLowerCase(),
          m.format('D').toLowerCase()
        ]
      }

      const matchesDate = formattedDates.some((date) =>
        date.includes(trimmedSearch)
      )

      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'Description':
          return description.includes(trimmedSearch)
        case 'LastModifiedDate':
          return matchesDate
        case 'LastModifiedBy':
          return modifiedBy.includes(trimmedSearch)
        case 'Id':
          return usersAssignedCount.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            description.includes(trimmedSearch) ||
            modifiedBy.includes(trimmedSearch) ||
            usersAssignedCount.includes(trimmedSearch) ||
            matchesDate
          )
      }
    })
  }, [profilesData, activeSearchTerm, searchColumn, salesforceAuditStore])

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData
    return [...filteredData].sort((a: any, b: any) => {
      if (sortConfig.key === 'Id') {
        const aCount = getUsersAssignedForProfile(a.Id).length
        const bCount = getUsersAssignedForProfile(b.Id).length
        return sortConfig.direction === 'asc'
          ? bCount - aCount
          : aCount - bCount
      }
      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = a.LastModifiedDate
          ? new Date(a.LastModifiedDate).getTime()
          : 0
        const dateB = b.LastModifiedDate
          ? new Date(b.LastModifiedDate).getTime()
          : 0
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA
      }
      const valA = a?.[sortConfig.key!] || ''
      const valB = b?.[sortConfig.key!] || ''
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return sortConfig.direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
      return 0
    })
  }, [filteredData, sortConfig, salesforceAuditStore])

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link:
          replaceUrl(
            routeConstants.SALESFORCE_AUDIT_DASHBOARD,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ) + '?page=1&tab=1'
      },
      {
        label: 'Profiles',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/profiles',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store.breadcrumbStore.getBreadcrumbsOptions()])

  const downloadProfiles = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string
    }

    try {
      const response = await downloadSalesforceProfilesReport(payload)
      const orgTZ = store.userStore.getOrganizationPreference().timeZone
      const profiles: any[] = response.data || []

      if (!profiles.length) {
        ToastNotification({
          type: 'error',
          message: 'No Profiles found for this audit'
        })
        return
      }

      const mapped = profiles.map((profile) => {
        return {
          Name: profile?.name || '-',
          UserAssigned: getUsersAssignedForProfile(profile?.id).length,
          Description: profile?.description || '-',
          User_Assigned: profile?.userCount || '0',
          LastModifiedDate:
            profile?.lastModifiedDate && profile?.lastModifiedDate !== '-'
              ? timeZone(
                  orgTZ,
                  profile?.lastModifiedDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          LastModifiedBy: profile?.lastModifiedByName || '-'
        }
      })

      const date = new Date()
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date
        .getDate()
        .toString()
        .padStart(2, '0')}-${date.getFullYear()}`

      convertJsonToCsvDownload(
        mapped,
        `Salesforce_Profiles_Report_${formattedDate}.csv`
      )

      ToastNotification({
        type: 'success',
        message: `Successfully exported ${mapped.length} Profile(s)`
      })
    } catch (error: any) {
      console.error('Profile Download Error:', error)

      ToastNotification({
        type: 'error',
        message:
          typeof error === 'string'
            ? error
            : error?.message || 'Error downloading Profiles report'
      })
    } finally {
      store.uiStore.setGlobalLoader(false)
      setLoading(false)
    }
  }

  const Columns = [
    {
      columnHeaderStyle: { width: '280px' },
      columnDataStyle: { width: '280px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Profile Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[280px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 25 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Users Assigned
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Id')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />

            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='flex justify-start text-start row items-start'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              {getUsersAssignedForProfile(text).length}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: { width: '220px' },
      columnDataStyle: { width: '220px' },
      heading: (
        <div className='flex whitespace-nowrap text-left justify-left space-x-[8px] items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Description
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Description')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Description' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Description' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Description',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <Tooltip
            id={`${text}-tooltip`}
            label={text}
            className={`override-tooltip-arrow max-w-[90%] break-all ${
              typeof text === 'string' && text.length > 20 ? text : '!hidden'
            }`}
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='tooltip-container sm:block overflow-hidden truncate min-w-[220px] max-w-[220px]'
              color={pallete.colors.textDark4}
            >
              {text ? text : '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text
              ? moment(text).format(timeFormats.shortMonthDateYearTime)
              : '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Modified by
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedBy',
      render: (record: any) => {
        const lastModifiedByName = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${lastModifiedByName}`}
              label={lastModifiedByName}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof lastModifiedByName === 'string' &&
                lastModifiedByName.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {lastModifiedByName}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(text)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]

  return (
    <>
      {loading || userLoading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='flex flex-col w-full py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())
                setCurrentPage(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPage(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'Profile Name', value: 'Name' },
                  { label: 'User Assigned', value: 'Id' },
                  { label: 'Description', value: 'Description' },
                  { label: 'Last Modified Date', value: 'LastModifiedDate' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPage(1)
                      setActiveSearchTerm(searchTerm)
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className='w-full flex justify-end mb-[16px]'>
            <Button
              id='outline-button'
              label='Export'
              disabled={
                store.uiStore.getGlobalLoader() || profilesData?.length === 0
              }
              type='neutral'
              size='small'
              onClick={() => {
                downloadProfiles()
              }}
              startIcon={<Download color={pallete.colors.neuButtonText} />}
            />
          </div>
          {sortedData?.length > 0 ? (
            <Table
              columns={Columns}
              data={paginate(
                sortedData,
                currentPage,
                SalesforceAuditStore.pageSize
              )}
              totalCount={sortedData?.length}
              currentPage={currentPage}
              rowsPerPage={SalesforceAuditStore.pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              clickable={(id) => {
                navigate(id)
              }}
              clickType='SalesforceProfiles'
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Profiles found for your search.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Profiles have been set up yet.</div>}
            />
          )}
        </div>
      )}
    </>
  )
}

export default observer(SFProfilesOverview)
